<script lang="ts">
import { legalDocument } from "@doc-cheap/legal";
import { error } from "@sveltejs/kit";
import SeoHead from "$lib/seo/SeoHead.svelte";

// One of the three published legal texts, as a page.
//
// The body is HTML because the text is rendered from its markdown when it is
// synced out of the repository it is authored in (scripts/sync-legal.mjs), not
// when a page is served: no markdown parser reaches the browser bundle, and a
// page that is built once at build time does no work per visit. The source is
// ours and the sync refuses one that carries markup of its own, so there is no
// untrusted input here to sanitise.
//
// The heading is the document's own, so the page adds none: a legal text starts
// with its title, and a second heading above it would be the page disagreeing
// with the document about what it is called.

let { slug }: { slug: string } = $props();

// Every caller is a route that names a slug this package publishes, so a miss
// is a route and a document that have gone out of step rather than anything a
// visitor did. It fails as a 500 at build time, which is where it is cheap.
const document = $derived.by(() => {
  const found = legalDocument(slug);
  if (!found) error(500, `no legal document is published at "${slug}"`);
  return found;
});
</script>

<SeoHead
  title={document.title}
  description={document.description}
  path={document.path}
/>

<main class="legal mx-auto max-w-3xl px-6 py-12">
  <!-- eslint-disable-next-line svelte/no-at-html-tags -->
  {@html document.html}
</main>

<style>
  /* The rendered document's own elements, which arrive as HTML and so are
     outside this component's markup — hence `:global`. Tailwind's reset takes
     every margin and every heading size off them, so a legal text without this
     is one undifferentiated column of 14px prose. The values are the site's:
     the same slate ink, the same measure, the same link colour. */
  .legal :global(h1) {
    font-size: 1.875rem;
    line-height: 2.25rem;
    font-weight: 600;
    letter-spacing: -0.025em;
  }
  .legal :global(h2) {
    margin-top: 2.5rem;
    font-size: 1.125rem;
    line-height: 1.75rem;
    font-weight: 600;
  }
  .legal :global(h3) {
    margin-top: 2rem;
    font-weight: 600;
  }
  .legal :global(p),
  .legal :global(ul),
  .legal :global(ol),
  .legal :global(table) {
    margin-top: 1rem;
    color: oklch(44.6% 0.03 256.802);
    line-height: 1.7;
  }
  .legal :global(ul),
  .legal :global(ol) {
    padding-left: 1.25rem;
  }
  .legal :global(ul) {
    list-style: disc;
  }
  .legal :global(ol) {
    list-style: decimal;
  }
  .legal :global(li) {
    margin-top: 0.375rem;
  }
  .legal :global(strong) {
    color: oklch(20.8% 0.042 265.755);
    font-weight: 600;
  }
  .legal :global(a) {
    color: oklch(20.8% 0.042 265.755);
    text-decoration: underline;
  }
  /* The header line each text opens with: effective date, version, address. */
  .legal :global(blockquote) {
    margin-top: 1.5rem;
    border-left: 3px solid oklch(92.9% 0.013 255.508);
    padding-left: 1rem;
    font-size: 0.875rem;
  }
  /* Three of the texts state their facts in tables; on a phone they scroll
     rather than squeezing a column to nothing. */
  .legal :global(table) {
    display: block;
    overflow-x: auto;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  .legal :global(th),
  .legal :global(td) {
    border: 1px solid oklch(92.9% 0.013 255.508);
    padding: 0.5rem 0.75rem;
    text-align: left;
    vertical-align: top;
  }
  .legal :global(code) {
    background: oklch(96.8% 0.007 247.896);
    border-radius: 0.25rem;
    padding: 0.1em 0.3em;
    font-size: 0.875em;
  }
</style>
